import { useMemo } from 'react';
import { getButtonLabel } from '../utils/flowButtons';
import { getNodeMeta } from '../constants/nodeTypes';

const TRIGGER_PREFIX = 'trigger.';

/**
 * Build hierarchical workflow tree from flat nodes + edges for list/tree views.
 */
export function buildWorkflowTree(definition) {
  const nodes = definition?.nodes || [];
  const edges = definition?.edges || [];
  if (!nodes.length) return [];

  const nodeById = {};
  nodes.forEach((n) => {
    if (n?.id) nodeById[n.id] = n;
  });

  const outEdges = {};
  edges.forEach((e) => {
    if (!e?.from) return;
    if (!outEdges[e.from]) outEdges[e.from] = [];
    outEdges[e.from].push(e);
  });

  const triggerIds = nodes
    .filter((n) => String(n.type || '').startsWith(TRIGGER_PREFIX))
    .map((n) => n.id);

  const entryIds = triggerIds.length
    ? triggerIds
    : nodes.filter((n) => !edges.some((e) => e.to === n.id)).map((n) => n.id);

  const visited = new Set();

  const walk = (nodeId, depth = 0, branchLabel = null, event = null, parentNodeId = null) => {
    if (!nodeId || !nodeById[nodeId]) return [];

    // Node already rendered earlier in the tree — emit a back-reference marker
    // instead of silently returning nothing, so the branch shows where it loops.
    if (visited.has(nodeId)) {
      const refNode = nodeById[nodeId];
      const refMeta = getNodeMeta(refNode.type);
      return [{
        key: `backref-${nodeId}-${depth}`,
        isBackRef: true,
        node: refNode,
        meta: refMeta,
        depth,
        branchLabel,
        event,
        parentNodeId, // which node's outgoing edge points here (for disconnecting)
        children: [],
      }];
    }
    visited.add(nodeId);

    visited.add(nodeId);

    const node = nodeById[nodeId];
    const meta = getNodeMeta(node.type);
    const item = {
      key: nodeId,
      node,
      meta,
      depth,
      branchLabel,
      event,
      children: [],
    };

    const outgoing = outEdges[nodeId] || [];
    const isCondition = node.type === 'logic.condition' || node.type === 'integration.api';
    const isRoute = node.type === 'logic.route_by_var' || node.type === 'ai.classify';

    if (isCondition) {
      const yesEdge = outgoing.find((e) => e.event === 'yes');
      const noEdge = outgoing.find((e) => e.event === 'no');
      const otherEdges = outgoing.filter((e) => !['yes', 'no'].includes(e.event));

      if (yesEdge?.to) {
        item.children.push({
          branchKey: `${nodeId}-yes`,
          branchLabel: node.type === 'integration.api' ? 'Success' : 'YES',
          branchType: 'yes',
          nodes: walk(yesEdge.to, depth + 1, node.type === 'integration.api' ? 'Success' : 'YES', 'yes', nodeId),
        });
      }
      if (noEdge?.to) {
        item.children.push({
          branchKey: `${nodeId}-no`,
          branchLabel: node.type === 'integration.api' ? 'Failure' : 'NO',
          branchType: 'no',
          nodes: walk(noEdge.to, depth + 1, node.type === 'integration.api' ? 'Failure' : 'NO', 'no', nodeId),
        });
      }
      otherEdges.forEach((e) => {
        if (e.to) {
          item.children.push({
            branchKey: `${nodeId}-${e.event}`,
            branchLabel: e.event,
            nodes: walk(e.to, depth + 1, e.event, e.event, nodeId),
          });
        }
      });
    } else if (isRoute) {
      outgoing.forEach((e) => {
        const label =
          e.match?.branch || e.match?.payload || e.event || 'route';
        if (e.to) {
          item.children.push({
            branchKey: `${nodeId}-${label}`,
            branchLabel: `${label} branch`,
            branchType: 'route',
            nodes: walk(e.to, depth + 1, `${label} branch`, e.event, nodeId),
          });
        }
      });
    } else if (
      node.type === 'message.template' ||
      node.type === 'message.interactive_buttons' ||
      node.type === 'message.list_preset' ||
      node.type === 'message.list_dynamic'
    ) {
      const buttonEdges = outgoing.filter((e) => e.event === 'button_reply');
      const otherEdges = outgoing.filter((e) => e.event !== 'button_reply');

      buttonEdges.forEach((e) => {
        const payload = e.match?.payload || '';
        let label = payload ? `Option: ${payload}` : 'Option';

        if (node.type === 'message.list_preset') {
          const row = (node.config?.rows || []).find(
            (r) => r.id === payload || r.label === payload || r.id === e.match?.id
          );
          if (row?.label) {
            label = `List: ${row.label}`;
          }
        } else if (node.type === 'message.list_dynamic') {
          const row = (node.config?.custom_rows || []).find(
            (r) => r.id === payload || r.label === payload || r.id === e.match?.id
          );
          if (row) {
            label = `Custom: ${row.label || row.id || 'Option'}`;
          } else {
            label = payload ? `Custom: ${payload}` : 'Custom option';
          }
        } else {
          const btn = (node.config?.buttons || []).find((b) => b.id === payload);
          label = btn
            ? `Button: ${getButtonLabel(btn)}`
            : payload
              ? `Button: ${payload}`
              : 'Button';
        }

        if (e.to) {
          item.children.push({
            branchKey: `${nodeId}-btn-${payload}`,
            branchLabel: label,
            branchType: 'route',
            nodes: walk(e.to, depth + 1, label, e.event, nodeId),
          });
        }
      });

      const primary =
        node.type === 'message.list_dynamic'
          // list_dynamic: dynamic selection is always user_input; never treat
          // a spurious completed edge as the primary dynamic-selection branch.
          ? otherEdges.find((e) => e.event === 'user_input')
          : otherEdges.find((e) =>
              ['completed', 'matched', 'valid', 'user_input'].includes(e.event)
            );
      const primaryTarget =
        primary?.to ??
        (node.type !== 'message.list_dynamic' ? otherEdges[0]?.to : undefined);

      // Only add "Then continue" / "On dynamic selection" if the target is not
      // already covered by a button branch
      const buttonTargets = new Set(buttonEdges.map((e) => e.to).filter(Boolean));
      if (primaryTarget && !buttonTargets.has(primaryTarget)) {
        const continueLabel =
          node.type === 'message.list_dynamic'
            ? 'On dynamic selection'
            : (node.type === 'message.template' || node.type === 'message.list_preset') &&
              buttonEdges.length > 0
              ? 'Then continue'
              : primary?.event || 'continue';
        item.children.push({
          branchKey: `${nodeId}-continue`,
          branchLabel: continueLabel,
          branchType: 'route',
          nodes: walk(primaryTarget, depth + 1, continueLabel, primary?.event, nodeId),
        });
      }
    } else {
      const primary = outgoing.find((e) =>
        ['completed', 'matched', 'valid', 'user_input'].includes(e.event)
      );
      const primaryTarget = primary?.to ?? outgoing[0]?.to;
      if (primaryTarget) {
        item.children = walk(primaryTarget, depth, branchLabel, primary?.event, nodeId);
      }
    }

    return [item];
  };

  const tree = [];
  entryIds.forEach((id) => {
    walk(id).forEach((item) => tree.push(item));
  });

  // Nodes not reachable from the walk (visited via DFS) — show as orphan
  // but attach any incoming edge sources so the badge can say "Used by: X"
  nodes.forEach((n) => {
    if (!visited.has(n.id)) {
      const incomingNodes = edges
        .filter((e) => e.to === n.id && e.from && nodeById[e.from])
        .map((e) => {
          const src = nodeById[e.from];
          const srcMeta = getNodeMeta(src.type);
          return srcMeta.listLabel?.(src.config) || srcMeta.label;
        });

      tree.push({
        key: n.id,
        node: n,
        meta: getNodeMeta(n.type),
        depth: 0,
        branchLabel: null,
        event: null,
        children: [],
        orphan: incomingNodes.length === 0,   // true orphan = zero incoming edges
        incomingNodes,                         // [] when truly disconnected
      });
    }
  });

  return tree;
}

export default function useWorkflowTree(definition) {
  return useMemo(() => buildWorkflowTree(definition), [definition]);
}
